import { useState } from 'react';
import { 
  Plug, 
  Plus, 
  Settings, 
  CheckCircle,
  Power,
  Database,
  Webhook,
  Key,
  Globe,
  Server,
  Code,
  RefreshCw,
  MessageSquare,
  Users,
  Briefcase,
  Headphones,
  GitBranch,
  Bot,
  Brain
} from 'lucide-react';
import { useNavigation } from '@/contexts/NavigationContext';
import { cn } from '@/lib/utils';
import { ComingSoonWatermark } from '@/components/ComingSoonWatermark';

interface PlatformIntegration {
  id: string;
  name: string;
  type: 'inbound' | 'outbound' | 'bidirectional';
  category: 'mcp' | 'api' | 'webhook' | 'database' | 'sso' | 'ai-communication' | 'ai-crm' | 'ai-support' | 'ai-devops';
  icon: any;
  status: 'active' | 'inactive' | 'error';
  description: string;
  endpoint?: string;
  lastSync?: Date;
  dataFlowIn?: number;
  dataFlowOut?: number;
  // AI-specific fields
  aiEnabled?: boolean;
  risksDetected?: number;
  channels?: string[];
  keywords?: string[];
  riskTypes?: string[];
}

const mockIntegrations: PlatformIntegration[] = [
  // AI Integrations (Themis Intelligence Layer)
  {
    id: 'ai-1',
    name: 'Sales Team Slack - AI Monitoring',
    type: 'inbound',
    category: 'ai-communication',
    icon: MessageSquare,
    status: 'active',
    description: 'Themis AI monitors Slack channels for emerging risks in sales conversations',
    endpoint: 'slack://workspace/sales-team',
    lastSync: new Date('2025-11-10T14:30:00Z'),
    dataFlowIn: 450,
    dataFlowOut: 0,
    aiEnabled: true,
    risksDetected: 12,
    channels: ['#sales-general', '#customer-negotiations', '#deal-reviews'],
    keywords: ['consumer duty', 'regulatory', 'compliance', 'high risk', 'exception', 'waiver'],
    riskTypes: ['Consumer Duty', 'Sales Practice', 'Regulatory Breach'],
  },
  {
    id: 'ai-2',
    name: 'Engineering Teams - AI Monitoring',
    type: 'inbound',
    category: 'ai-communication',
    icon: Users,
    status: 'active',
    description: 'Themis AI monitors Teams channels for security and operational risks',
    endpoint: 'teams://tenant/engineering',
    lastSync: new Date('2025-11-10T15:45:00Z'),
    dataFlowIn: 380,
    dataFlowOut: 0,
    aiEnabled: true,
    risksDetected: 8,
    channels: ['Product Development', 'Platform Engineering', 'DevOps'],
    keywords: ['security', 'data breach', 'vulnerability', 'urgent fix', 'critical bug'],
    riskTypes: ['Cyber Security', 'Operational', 'Technology'],
  },
  {
    id: 'ai-3',
    name: 'Salesforce CRM - AI Monitoring',
    type: 'inbound',
    category: 'ai-crm',
    icon: Briefcase,
    status: 'active',
    description: 'Themis AI analyzes CRM data for customer complaints and regulatory concerns',
    endpoint: 'https://api.salesforce.com/themis-integration',
    lastSync: new Date('2025-11-10T16:00:00Z'),
    dataFlowIn: 820,
    dataFlowOut: 0,
    aiEnabled: true,
    risksDetected: 23,
    channels: ['Opportunities', 'Accounts', 'Cases'],
    keywords: ['complaint', 'escalation', 'legal', 'regulatory concern', 'mis-selling'],
    riskTypes: ['Consumer Duty', 'Reputational', 'Legal'],
  },
  {
    id: 'ai-4',
    name: 'Zendesk Support - AI Monitoring',
    type: 'inbound',
    category: 'ai-support',
    icon: Headphones,
    status: 'active',
    description: 'Themis AI monitors support tickets for customer complaints and conduct risks',
    endpoint: 'https://api.zendesk.com/themis-integration',
    lastSync: new Date('2025-11-10T16:15:00Z'),
    dataFlowIn: 1200,
    dataFlowOut: 0,
    aiEnabled: true,
    risksDetected: 34,
    channels: ['Customer Complaints', 'Escalations', 'Refunds'],
    keywords: ['complaint', 'refund', 'unfair', 'misleading', 'discrimination', 'vulnerable customer', 'legal action'],
    riskTypes: ['Consumer Duty', 'Conduct Risk', 'Reputational', 'Legal'],
  },
  {
    id: 'ai-5',
    name: 'GitHub Repositories - AI Monitoring',
    type: 'inbound',
    category: 'ai-devops',
    icon: GitBranch,
    status: 'inactive',
    description: 'Themis AI monitors GitHub for security vulnerabilities and breaking changes',
    endpoint: 'https://api.github.com/themis-integration',
    lastSync: new Date('2025-11-08T10:00:00Z'),
    dataFlowIn: 0,
    dataFlowOut: 0,
    aiEnabled: false,
    risksDetected: 0,
    channels: ['line4-platform', 'risk-engine', 'compliance-api'],
    keywords: ['breaking change', 'security vulnerability', 'urgent deployment'],
    riskTypes: ['Technology', 'Operational'],
  },
  
  // Platform Integrations
  {
    id: '1',
    name: 'Model Context Protocol Server',
    type: 'bidirectional',
    category: 'mcp',
    icon: Server,
    status: 'active',
    description: 'MCP server enabling AI assistants to access Line4 risk data and perform actions',
    endpoint: 'mcp://localhost:3000',
    lastSync: new Date('2025-11-10T16:30:00Z'),
    dataFlowIn: 156,
    dataFlowOut: 89,
  },
  {
    id: '2',
    name: 'External Risk API',
    type: 'inbound',
    category: 'api',
    icon: Code,
    status: 'active',
    description: 'REST API for external systems to submit risk events and incidents',
    endpoint: 'https://api.line4.ai/v1/risks',
    lastSync: new Date('2025-11-10T16:45:00Z'),
    dataFlowIn: 2847,
    dataFlowOut: 0,
  },
  {
    id: '3',
    name: 'Compliance Webhooks',
    type: 'outbound',
    category: 'webhook',
    icon: Webhook,
    status: 'active',
    description: 'Webhook notifications for compliance status changes and alerts',
    endpoint: 'https://webhooks.company.com/compliance',
    lastSync: new Date('2025-11-10T16:20:00Z'),
    dataFlowIn: 0,
    dataFlowOut: 456,
  },
  {
    id: '4',
    name: 'Data Warehouse Sync',
    type: 'outbound',
    category: 'database',
    icon: Database,
    status: 'active',
    description: 'Automated sync of risk metrics and KPIs to enterprise data warehouse',
    endpoint: 'postgresql://warehouse.company.com:5432/analytics',
    lastSync: new Date('2025-11-10T15:00:00Z'),
    dataFlowIn: 0,
    dataFlowOut: 12500,
  },
  {
    id: '5',
    name: 'SSO Authentication',
    type: 'inbound',
    category: 'sso',
    icon: Key,
    status: 'active',
    description: 'Single Sign-On integration with corporate identity provider',
    endpoint: 'https://sso.company.com/saml',
    lastSync: new Date('2025-11-10T16:50:00Z'),
    dataFlowIn: 892,
    dataFlowOut: 0,
  },
  {
    id: '6',
    name: 'Public API Gateway',
    type: 'bidirectional',
    category: 'api',
    icon: Globe,
    status: 'inactive',
    description: 'Public-facing API for external partners and third-party integrations',
    endpoint: 'https://public-api.line4.ai/v1',
    lastSync: new Date('2025-11-08T10:00:00Z'),
    dataFlowIn: 0,
    dataFlowOut: 0,
  },
];

const getStatusColor = (status: string) => {
  switch (status) {
    case 'active':
      return 'text-success-500 bg-success-500/20 border-success-500/30';
    case 'inactive':
      return 'text-neutral-500 bg-neutral-500/20 border-neutral-500/30';
    case 'error':
      return 'text-danger-500 bg-danger-500/20 border-danger-500/30';
    default:
      return 'text-neutral-500 bg-neutral-500/20 border-neutral-500/30';
  }
};

const getTypeColor = (type: string) => {
  switch (type) {
    case 'inbound':
      return 'text-primary-400 bg-primary-500/20 border-primary-500/30';
    case 'outbound':
      return 'text-warning-400 bg-warning-500/20 border-warning-500/30';
    case 'bidirectional':
      return 'text-purple-400 bg-purple-500/20 border-purple-500/30';
    default:
      return 'text-neutral-400 bg-neutral-500/20 border-neutral-500/30';
  }
};

export function AdminIntegrationsPage() {
  const { sectionColor } = useNavigation();
  const [integrations] = useState<PlatformIntegration[]>(mockIntegrations);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const activeIntegrations = integrations.filter(i => i.status === 'active').length;
  const totalDataFlowIn = integrations.reduce((sum, i) => sum + (i.dataFlowIn || 0), 0);
  const totalDataFlowOut = integrations.reduce((sum, i) => sum + (i.dataFlowOut || 0), 0);
  const aiIntegrations = integrations.filter(i => i.category.startsWith('ai-'));
  const aiRisksDetected = aiIntegrations.reduce((sum, i) => sum + (i.risksDetected || 0), 0);

  // Group integrations by category
  const aiMonitoring = integrations.filter(i => i.category.startsWith('ai-'));
  const platformIntegrations = integrations.filter(i => !i.category.startsWith('ai-'));
  
  const filteredIntegrations = selectedCategory === 'all' 
    ? integrations 
    : selectedCategory === 'ai' 
    ? aiMonitoring 
    : platformIntegrations;

  return (
    <div className="space-y-8 animate-fadeIn">
      <ComingSoonWatermark
        pageKey="admin-integrations"
        icon={Plug}
        title="Platform Integrations"
        description="Manage all inbound and outbound integrations including MCP server configuration"
      />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-primary-500/20 to-cyan-500/20 border-2 border-primary-500/30">
            <Plug className="w-8 h-8 text-primary-500" />
          </div>
          <div>
            <h1 className={cn("text-3xl font-bold", sectionColor)}>
              Platform Integrations
            </h1>
            <p className="text-neutral-400 mt-2">
              Manage all integrations: AI risk detection, APIs, webhooks, and MCP server configuration
            </p>
          </div>
        </div>
        <div className="p-3 rounded-xl bg-gradient-to-br from-primary-500/20 to-cyan-500/20 border-2 border-primary-500/30">
          <Plug className="w-8 h-8 text-primary-500" />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-success-500/20 rounded-lg">
              <Plug className="w-5 h-5 text-success-500" />
            </div>
            <div>
              <p className="text-sm text-neutral-400">Active Integrations</p>
              <p className="text-2xl font-bold text-white">{activeIntegrations}</p>
            </div>
          </div>
          <p className="text-xs text-neutral-500">Currently connected</p>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Brain className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <p className="text-sm text-neutral-400">AI Risks Detected</p>
              <p className="text-2xl font-bold text-white">{aiRisksDetected}</p>
            </div>
          </div>
          <p className="text-xs text-neutral-500">By Themis Intelligence</p>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary-500/20 rounded-lg">
              <Database className="w-5 h-5 text-primary-500" />
            </div>
            <div>
              <p className="text-sm text-neutral-400">Data Flow In</p>
              <p className="text-2xl font-bold text-white">{totalDataFlowIn.toLocaleString()}</p>
            </div>
          </div>
          <p className="text-xs text-neutral-500">Events received (24h)</p>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-warning-500/20 rounded-lg">
              <Webhook className="w-5 h-5 text-warning-500" />
            </div>
            <div>
              <p className="text-sm text-neutral-400">Data Flow Out</p>
              <p className="text-2xl font-bold text-white">{totalDataFlowOut.toLocaleString()}</p>
            </div>
          </div>
          <p className="text-xs text-neutral-500">Events sent (24h)</p>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Server className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <p className="text-sm text-neutral-400">MCP Server</p>
              <p className="text-2xl font-bold text-success-400">Active</p>
            </div>
          </div>
          <p className="text-xs text-neutral-500">Model Context Protocol</p>
        </div>
      </div>

      {/* Category Filter & Integration Cards */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold text-white">All Integrations</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelectedCategory('all')}
                className={cn(
                  "px-3 py-1.5 rounded-lg text-sm font-medium transition-colors",
                  selectedCategory === 'all'
                    ? "bg-primary-500 text-black"
                    : "bg-neutral-800 text-neutral-400 hover:bg-neutral-700"
                )}
              >
                All ({integrations.length})
              </button>
              <button
                onClick={() => setSelectedCategory('ai')}
                className={cn(
                  "px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5",
                  selectedCategory === 'ai'
                    ? "bg-purple-500 text-black"
                    : "bg-neutral-800 text-neutral-400 hover:bg-neutral-700"
                )}
              >
                <Bot className="w-4 h-4" />
                AI Monitoring ({aiMonitoring.length})
              </button>
              <button
                onClick={() => setSelectedCategory('platform')}
                className={cn(
                  "px-3 py-1.5 rounded-lg text-sm font-medium transition-colors",
                  selectedCategory === 'platform'
                    ? "bg-primary-500 text-black"
                    : "bg-neutral-800 text-neutral-400 hover:bg-neutral-700"
                )}
              >
                Platform ({platformIntegrations.length})
              </button>
            </div>
          </div>
          <button className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Add Integration
          </button>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {filteredIntegrations.map((integration) => (
            <div
              key={integration.id}
              className="card hover:border-primary-500/50 transition-all"
            >
              <div className="flex items-start justify-between">
                {/* Left Side - Integration Info */}
                <div className="flex items-start gap-4 flex-1">
                  <div className={cn(
                    "p-3 rounded-lg",
                    integration.status === 'active' ? "bg-primary-500/20" : "bg-neutral-800"
                  )}>
                    <integration.icon className={cn(
                      "w-6 h-6",
                      integration.status === 'active' ? "text-primary-400" : "text-neutral-500"
                    )} />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-white">{integration.name}</h3>
                      <span className={`badge ${getTypeColor(integration.type)} text-xs`}>
                        {integration.type}
                      </span>
                      <span className={`badge ${getStatusColor(integration.status)} text-xs`}>
                        {integration.status}
                      </span>
                    </div>
                    
                    <p className="text-sm text-neutral-400 mb-3">
                      {integration.description}
                    </p>
                    
                    {integration.endpoint && (
                      <div className="flex items-center gap-2 text-xs text-neutral-500 font-mono bg-neutral-900 px-3 py-2 rounded">
                        <Globe className="w-3 h-3" />
                        <span>{integration.endpoint}</span>
                      </div>
                    )}
                    
                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-4 mt-4">
                      {integration.aiEnabled ? (
                        <>
                          <div>
                            <p className="text-xs text-neutral-400 mb-1">Risks Detected</p>
                            <p className="text-sm text-danger-400 font-medium">
                              {integration.risksDetected || 0}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-neutral-400 mb-1">Channels</p>
                            <p className="text-sm text-white font-medium">
                              {integration.channels?.length || 0}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-neutral-400 mb-1">Keywords</p>
                            <p className="text-sm text-white font-medium">
                              {integration.keywords?.length || 0}
                            </p>
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <p className="text-xs text-neutral-400 mb-1">Category</p>
                            <p className="text-sm text-white font-medium uppercase">{integration.category}</p>
                          </div>
                          <div>
                            <p className="text-xs text-neutral-400 mb-1">Data In (24h)</p>
                            <p className="text-sm text-primary-400 font-medium">
                              {integration.dataFlowIn?.toLocaleString() || '0'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-neutral-400 mb-1">Data Out (24h)</p>
                            <p className="text-sm text-warning-400 font-medium">
                              {integration.dataFlowOut?.toLocaleString() || '0'}
                            </p>
                          </div>
                        </>
                      )}
                    </div>

                    {/* AI Keywords Section */}
                    {integration.aiEnabled && integration.keywords && (
                      <div className="mt-4 pt-4 border-t border-neutral-800">
                        <p className="text-xs text-neutral-400 mb-2">Monitored Keywords:</p>
                        <div className="flex flex-wrap gap-2">
                          {integration.keywords.slice(0, 5).map((keyword, idx) => (
                            <span key={idx} className="badge badge-primary text-xs">
                              {keyword}
                            </span>
                          ))}
                          {integration.keywords.length > 5 && (
                            <span className="text-xs text-neutral-500">
                              +{integration.keywords.length - 5} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Side - Actions */}
                <div className="flex flex-col gap-2 ml-4">
                  <button className="btn-outline text-xs flex items-center gap-1 px-3 py-2">
                    <Settings className="w-3 h-3" />
                    Configure
                  </button>
                  <button className="btn-outline text-xs flex items-center gap-1 px-3 py-2">
                    <RefreshCw className="w-3 h-3" />
                    Test
                  </button>
                  {integration.status === 'active' ? (
                    <button className="btn-outline text-xs flex items-center gap-1 px-3 py-2 text-danger-400 border-danger-500/30 hover:bg-danger-500/10">
                      <Power className="w-3 h-3" />
                      Disable
                    </button>
                  ) : (
                    <button className="btn-outline text-xs flex items-center gap-1 px-3 py-2 text-success-400 border-success-500/30 hover:bg-success-500/10">
                      <Power className="w-3 h-3" />
                      Enable
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* MCP Server Configuration */}
      <div className="card border-2 border-purple-500/30 bg-gradient-to-br from-purple-500/5 to-transparent">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500">
            <Server className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-lg font-semibold text-white">Model Context Protocol (MCP) Server</h3>
              <span className="badge badge-success text-xs">Active</span>
            </div>
            <div className="space-y-2 text-sm text-neutral-300">
              <p>
                The MCP server enables AI assistants (Claude, ChatGPT, etc.) to interact with Line4's risk management data and perform operations through a standardized protocol.
              </p>
              <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-purple-500/20">
                <div>
                  <p className="text-xs text-neutral-400 mb-1">Server Status</p>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-success-500" />
                    <span className="text-sm font-medium text-white">Running on port 3000</span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-neutral-400 mb-1">Active Connections</p>
                  <p className="text-lg font-bold text-purple-400">3</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-400 mb-1">Tools Available</p>
                  <p className="text-lg font-bold text-cyan-400">12</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-400 mb-1">Last Request</p>
                  <p className="text-sm text-white">2 minutes ago</p>
                </div>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button className="btn-outline text-xs flex items-center gap-1">
                <Settings className="w-3 h-3" />
                Configure MCP
              </button>
              <button className="btn-outline text-xs flex items-center gap-1">
                <Code className="w-3 h-3" />
                View Documentation
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
